import { motion } from "framer-motion";
import { Shell, Fish, Droplet, Anchor, Sparkles, Waves, Medal, Trophy, GraduationCap } from "lucide-react";

const stages = [
  { icon: <Shell size={22} />, name: "Urchins", phase: "First feelings of water", note: "Feeling safe, supported & happy — where trust begins.", accent: "#4FA3C6" },
  { icon: <Fish size={22} />, name: "Small Fry", phase: "Comfort, calm & connection", note: "Confidence grows through play, routine & curiosity.", accent: "#6BAFCC" },
  { icon: <Droplet size={22} />, name: "Guppies", phase: "Curiosity & first movements", note: "Splashing, reaching & growing water confidence.", accent: "#3E9AB8" },
  { icon: <Droplet size={22} />, name: "Tiny", phase: "Early independent skills", note: "Floating, kicking & breath control, with support.", accent: "#1F7A8C" },
  { icon: <Anchor size={22} />, name: "Tetra", phase: "Strength, balance & body awareness", note: "Coordination, balance & learning to stay safe.", accent: "#6ED3C3" },
  { icon: <Sparkles size={22} />, name: "Tots", phase: "Independent movement begins", note: "First short solo swims & simple breathing.", accent: "#FF8C6B" },
  { icon: <Waves size={22} />, name: "Baby Sharks", phase: "Real swimming starts", note: "Arms, legs & breathing together — real distance.", accent: "#F59E0B" },
  { icon: <Medal size={22} />, name: "Sharks 1", phase: "Strong, safe & independent", note: "Confident and capable in deeper water.", accent: "#4FA3C6" },
  { icon: <Trophy size={22} />, name: "Sharks 2", phase: "Technique, endurance & efficiency", note: "Smoother strokes, longer distances.", accent: "#1F7A8C" },
  { icon: <GraduationCap size={22} />, name: "Sharks 3", phase: "Graduation", note: "Confident, skilled swimmers ready for life.", accent: "#0A2540", grad: true },
];

export const SwimJourney = () => {
  return (
    <section className="pt-8 md:pt-10 pb-16 md:pb-24" data-testid="swim-journey">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">The Happy Tiddlers journey</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
            From first splash to graduation
          </h2>
          <p className="mt-5 text-lg text-[#334155]">
            Every child follows the same joyful path — ten gentle stages that turn nervous
            first-timers into confident, capable swimmers for life.
          </p>
        </div>

        <div className="relative">
          {/* connecting line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-1 md:-translate-x-1/2 rounded-full bg-gradient-to-b from-[#38BDF8] via-[#10B981] to-[#0A2540]" aria-hidden="true" />

          <div className="space-y-6 md:space-y-0">
            {stages.map((s, i) => {
              const left = i % 2 === 0;
              return (
                <div
                  key={s.name}
                  className="relative md:grid md:grid-cols-2 md:gap-16 md:items-center md:min-h-[6.5rem]"
                  data-testid={`journey-stage-${i}`}
                >
                  {/* node */}
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-3 md:top-1/2 md:-translate-y-1/2 z-10">
                    <span
                      className="grid place-items-center w-12 h-12 rounded-2xl text-white shadow-lg ring-4 ring-[#F8FAFC]"
                      style={{ backgroundColor: s.accent }}
                    >
                      {s.icon}
                    </span>
                  </div>

                  {/* card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5 }}
                    className={`pl-20 md:pl-0 ${left ? "md:pr-16 md:text-right md:col-start-1" : "md:pl-16 md:col-start-2"}`}
                  >
                    <div className={`inline-block w-full rounded-[1.5rem] p-6 border shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${s.grad ? "bg-[#0A2540] border-[#0A2540]" : "bg-white border-slate-100"}`}>
                      <div className={`flex items-center gap-2 flex-wrap ${left ? "md:justify-end" : ""}`}>
                        <span className={`text-xs font-black ${s.grad ? "text-white/40" : "text-slate-300"}`}>{String(i + 1).padStart(2, "0")}</span>
                        <h3 className={`font-heading text-xl font-bold ${s.grad ? "text-white" : "text-[#0F172A]"}`}>{s.name}</h3>
                        {s.grad && (
                          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF7A1A] text-white px-2.5 py-1 rounded-full">Graduation</span>
                        )}
                      </div>
                      <p className="mt-1 text-sm font-bold uppercase tracking-wide" style={{ color: s.grad ? "#5EEAD4" : s.accent }}>{s.phase}</p>
                      <p className={`mt-2 leading-relaxed ${s.grad ? "text-[#CBD5E1]" : "text-[#64748B]"}`}>{s.note}</p>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
